import { faCircleInfo } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Link } from 'react-router-dom'
import Checkbox from '../../../../../../../../forms/Checkbox'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { makeVariantGroupMainIdLast, sortVariantProducts } from '../../../../../../../../utils/globals/product'
import withLoading from '../../../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../utils/reducers'
import { getConfigParam } from '../../../../../../../../utils/reducers/getters'
import { VariantsPayload } from '../../../../../../../../utils/reducers/products/variants'
import ModalCreateEdit from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../../baseComponents/ModalsLayouts/types'
import TwinIcon from '../../../../../../../../baseComponents/TwinIcon'
import { createModalComplexProductMatrixStructure } from './functions'
import ModalComplexProductOrderTable from './ModalComplexProductOrderTable'
import { ModalComplexProductOrderTableConfigBase } from './ModalComplexProductOrderTable/types'
import { ModalComplexProductOrderParsedData } from './types'
import { changeErrorMessage } from '../../../../../../../../utils/reducers/reduxDispatch'
import { BuildedOrder, RowData } from '../../../../../../../../baseComponents/TwinTable/types'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../../../forms/TwinForm/types'
import LoadingSpinner from '../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { sortTableDataByNLines } from '../ModalEditAddonProductOrder/functions'
import locations from '../../../../../../../../utils/globals/locations'
import { OrderURLsTable } from '../../../../types'

type ModalComplexProductOrderProps = ModalComplexProductOrderLogicProps & {
}

const ModalComplexProductOrder: React.FC<ModalComplexProductOrderProps> = ({ setOpened, allRowData, headerData, orderURLs, ...rest }) => {
    const { config, setConfig, onPreSubmit, myOnSubmit, main, ...all } = useModalComplexProductOrderLogic({ setOpened, allRowData, orderURLs, ...rest, headerData })
    const { t } = useTwinTranslation()
    return (
        <ModalCreateEdit fields={[]} url={orderURLs?.createData[0]} setOpened={setOpened} translations={{ title: <TitleModal allRowData={allRowData} config={config} setConfig={setConfig} />, button: t('save', 'Guardar') }} haveButtonPermissions={true} {...rest} onSubmit={myOnSubmit} beforeSubmit={onPreSubmit}>
            {main ? <ModalComplexProductOrderTable {...all} main={main} variants={rest.variants} config={{ CurrencyId: headerData.CurrencyId, ...config }} productName={allRowData?.['ProductLangs.name'] || allRowData?.['name']} /> : <LoadingSpinner />}
        </ModalCreateEdit>
    )
}

type ModalComplexProductOrderLogicProps = ModalEditComponentProps & ReduxVariants & {
    orderURLs: OrderURLsTable
    getCompleteData: (order?: BuildedOrder) => Promise<void>
    headerData: TwinDictionary
    tableData: TwinDictionary[]
}

const useModalComplexProductOrderLogic = ({ headerData, allRowData, variants, orderURLs, setOpened, getCompleteData, tableData, onSubmit }: ModalComplexProductOrderLogicProps) => {
    const [config, setConfig] = useState<ModalComplexProductOrderTableConfigBase>({ showReference: getConfigParam('tableUnitsComplexProductShowReference') === 'on', showPrice: getConfigParam('tableUnitsComplexProductShowPrice') === 'on' })
    const [productData, setProductData] = useState<ModalComplexProductOrderParsedData | null>(null)
    const [columns, setColumns] = useState<number[]>([])
    const [main, setMain] = useState<number | null>(null)
    const [variantAddons, setVariantAddons] = useState<TwinDictionary[]>([])
    const [orderVariants, setOrderVariants] = useState<null | TwinDictionary>(null)
    const { t } = useTwinTranslation()
    const mustHaveQuantityToCreateText = t('mustHaveQuantityToCreate', 'Debes introducir cantidad para crear')
    const getData = useCallback(async () => {
        const params: TwinDictionary = {}
        for (const key of orderURLs?.getSubProducts[1]) {
            params[key] = headerData[key]
        }
        let subProducts = await twinFetchPostJSON(orderURLs?.getSubProducts[0], { 'ProductId': allRowData?.ProductId, ...params })
        const { orderedGroupsJson, myMain } = makeVariantGroupMainIdLast(variants?.orderedGroupsJson || {}, allRowData?.VariantGroupMainId, subProducts)
        const variantsParsed: any = { ...variants, orderedGroupsJson }
        subProducts = sortVariantProducts(subProducts, variantsParsed)
        let myOrder: TwinDictionary = {}
        for (const key in subProducts) {
            const subProduct = subProducts[key]
            myOrder[subProduct.id] = key
        }
        const currentDictionary: TwinDictionary = {}
        const variantAddonsArr: TwinDictionary[] = []
        if (allRowData?.completeData?.tableData) {
            for (const element of allRowData?.completeData?.tableData || {}) {
                if (element.OrderVariantGroupId === allRowData?.OrderVariantGroupId) {
                    if(!element.OrderProductId){
                        currentDictionary[element['ProductId']] =  element
                    }else {
                        variantAddonsArr.push(element)
                    }
                }
            }
        }
        const parentName = allRowData?.['ProductLangs.name'] || allRowData?.name
        const { rows, usedVariants } = createModalComplexProductMatrixStructure(subProducts, currentDictionary, variants, myMain, parentName)
        setProductData(rows)
        setVariantAddons(variantAddonsArr)
        setColumns(Array.from(usedVariants))
        setMain(myMain)
        setOrderVariants(myOrder)
    }, [allRowData, variants, headerData, orderURLs?.getSubProducts])


    const onPreSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const result: TwinDictionary[] = []
        const deletedProducts = new Set<number>()
        if (productData) {
            let myLineId = 0
            for (let row of productData) {
                for (let columnKey in row) {
                    const column = row[columnKey]
                    const isValidForPush = typeof column === 'object' && (column.LineId || (column.quantity && column.quantity !== '0'))
                    if (isValidForPush) {
                        const myQuantity = parseFloat(String(column.quantity || '0'))
                        const isChanging = parseFloat(String(column["_oldQuantity"] || '0')) !== myQuantity
                        result.push({
                            ...column,
                            ProductId: column.id,
                            quantity: myQuantity,
                            initialUnitPriceWithoutTax: parseFloat(String(column.initialUnitPriceWithoutTax || 0)),
                            id: column.LineId,
                            isChanging,
                            ...orderURLs?.createData[1],
                        })
                        if (column.LineId && myQuantity === 0){
                            deletedProducts.add(column.LineId)
                        }
                        if (column.LineId && myQuantity && column.LineId > myLineId) {
                            myLineId = column.LineId
                        }
                    }
                }
            }
            let lastAccoundId = null
            let lastAlternativeId = undefined
            for (let i = tableData?.length - 1; i > -1; i--) {
                if (tableData?.[i].id && tableData?.[i].OrderId && tableData?.[i].id === myLineId) {
                    lastAccoundId = tableData?.[i]?.OrderAccountId
                    break
                } else if (tableData?.[i].id && tableData?.[i].OrderId && lastAlternativeId === undefined) {
                    lastAlternativeId = tableData?.[i]?.OrderAccountId || null
                }
            }
            const OrderVariantGroupId = allRowData?.OrderVariantGroupId || null
            for (const key in result) {
                const myItem = result[key]
                if (!myItem.LineId) {
                    myItem.OrderAccountId = lastAccoundId || lastAlternativeId
                    myItem.OrderVariantGroupId = OrderVariantGroupId
                }
            }
        }
        if(deletedProducts.size){
            for (const key in variantAddons) {
                let myVariantAddon = variantAddons[key]
                if (myVariantAddon.OrderProductId && deletedProducts.has(myVariantAddon.OrderProductId)){
                    myVariantAddon.quantity = 0
                }
            }
        }
        const sortedByNLines = sortTableDataByNLines([...result, ...variantAddons], orderVariants || {})
        vals.createProductBulk = sortedByNLines
        vals.SuperProductParentId = allRowData?.ProductId
        if (vals.createProductBulk.length === 0) {
            changeErrorMessage(mustHaveQuantityToCreateText)
            return false
        }
        return true
    }, [productData, orderURLs?.createData, tableData, mustHaveQuantityToCreateText, allRowData?.OrderVariantGroupId, variantAddons, orderVariants, allRowData?.ProductId])

    const myOnSubmit: OnSubmit = useCallback(async (res, values) => {
        if (onSubmit) {
            await onSubmit(res, values)
        } else {
            await getCompleteData()
        }
        if (!allRowData?.OrderVariantGroupId){
            setTimeout(() => {
                //@ts-ignore
                document.querySelector('.add_product_row.input_table_header_row')?.click()
            }, 100)
        }
        setOpened(null)
    }, [getCompleteData, setOpened, onSubmit, allRowData?.OrderVariantGroupId])

    useEffect(() => {
        getData()
    }, [getData])

    return { productData, columns, main, setProductData, config, setConfig, onPreSubmit, myOnSubmit }
}

interface TitleModalProps extends TitleModalLogicProps {
    allRowData?: RowData
    config: ModalComplexProductOrderTableConfigBase
}

const TitleModal: React.FC<TitleModalProps> = ({ allRowData, config, setConfig }) => {
    const { t } = useTwinTranslation()
    const { changeConfig } = useTitleModalLogic({ setConfig })
    return (
        <div className='flex items-center gap-x-10'>
            <h2 className='twin_two_lines_elipsis'>{allRowData?.['ProductLangs.name'] || allRowData?.['name']} - {allRowData?.['reference']}</h2>
            <Link to={locations.product +'/' + allRowData?.id + '/variants'} style={{ height: 18 }} target='_blank'><TwinIcon icon={faCircleInfo} fontSize={18} className='ml-10 cursor-pointer' /></Link>
            <div className='ml-auto flex items-center'>
                <Checkbox label={t('showReferences', 'Mostrar referencias')} checked={config.showReference} onChange={(check) => changeConfig(check, 'showReference')} />
            </div>
        </div>
    )
}

interface TitleModalLogicProps {
    setConfig: React.Dispatch<React.SetStateAction<ModalComplexProductOrderTableConfigBase>>
}

const useTitleModalLogic = ({ setConfig }: TitleModalLogicProps) => {
    const changeConfig = useCallback((check: boolean, key: keyof ModalComplexProductOrderTableConfigBase) => {
        setConfig((oldConfig) => {
            return {
                ...oldConfig,
                [key]: check
            }
        })
    }, [setConfig])

    return { changeConfig}
}


const VariantsDispatch = {
    setVariants: (payload: VariantsPayload) => ({ type: 'CHANGE_VARIANTS', payload }),
}

export type ReduxVariants = ConnectedProps<typeof VariantsConnect>
const mapVariantsConnector = (state: AllReduxPayloads) => ({ variants: state.variants })
const VariantsConnect = connect(mapVariantsConnector, VariantsDispatch)

const VariantsConnectLoading = withLoading(ModalComplexProductOrder, [{ fetchUrl: '/api/app/product/productVariant/getAllVariantsComplete', propName: 'variants', setFunctionName: 'setVariants' }])

const ModalComplexProductOrderConnect = VariantsConnect(VariantsConnectLoading)

export default ModalComplexProductOrderConnect